import React, { useState, useRef, useEffect } from "react";
import { Trash, X, MessageSquare, ChevronLeft, ChevronRight } from "lucide-react";
import { createComment, deleteComment } from "../../../utils/Api";
import { Comment, Reply } from "./CommentThread";

export interface PendingPin {
  pageId: string;
  xFrac: number;
  yFrac: number;
}

interface Props {
  comments: Comment[];
  documentId: string;
  currentUserEmail: string | null;
  pendingPin: PendingPin | null;
  selectedCommentId: string | null;
  pages: string[];
  onCommentsChanged: (updated: Comment[]) => void;
  onPendingPinClear: () => void;
  onSelectComment: (id: string | null) => void;
}

function initials(name: string): string {
  return (name.split("@")[0] ?? name)
    .split(/[._-]/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("");
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortName(email: string): string {
  return (email.split("@")[0] ?? email).replace(/[._-]/g, " ");
}

const Avatar: React.FC<{ name: string; size?: "sm" | "xs" }> = ({ name, size = "sm" }) => (
  <div
    className={`rounded-full bg-[#1d365d] text-white font-bold flex items-center justify-center shrink-0 select-none ${
      size === "xs" ? "w-5 h-5 text-[9px]" : "w-6 h-6 text-[10px]"
    }`}
  >
    {initials(name)}
  </div>
);

// ─── Individual comment card ──────────────────────────────────────────────────
const CommentCard: React.FC<{
  comment: Comment;
  documentId: string;
  currentUserEmail: string | null;
  isSelected: boolean;
  onSelect: () => void;
  onUpdated: (c: Comment) => void;
  onDeleted: (id: string) => void;
}> = ({ comment, documentId, currentUserEmail, isSelected, onSelect, onUpdated, onDeleted }) => {
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const replyRef = useRef<HTMLTextAreaElement>(null);

  const handleReply = async () => {
    const text = replyText.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await createComment(documentId, {
        page_id: comment.page_id,
        text,
        x_position: comment.x_position,
        y_position: comment.y_position,
        parent_id: comment.id,
      });
      const newReply: Reply = {
        id: data.id,
        author_id: data.author_id,
        author_name: data.author_name,
        text: data.text,
        created_at: data.created_at,
      };
      onUpdated({ ...comment, replies: [...comment.replies, newReply] });
      setReplyText("");
    } catch (e) {
      console.error("Failed to post reply", e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReply = async (replyId: string) => {
    try {
      await deleteComment(documentId, replyId);
      onUpdated({ ...comment, replies: comment.replies.filter((r) => r.id !== replyId) });
    } catch (e) {
      console.error("Failed to delete reply", e);
    }
  };

  const handleDeleteRoot = async () => {
    try {
      await deleteComment(documentId, comment.id);
      onDeleted(comment.id);
    } catch (e) {
      console.error("Failed to delete comment", e);
    }
  };

  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      className={`border-l-[3px] rounded-r-lg bg-white shadow-sm cursor-pointer transition-all mb-2 mx-2 overflow-hidden ${
        isSelected
          ? "border-[#1d365d] shadow-md ring-1 ring-[#1d365d]/20"
          : "border-amber-400 hover:border-amber-500 hover:shadow"
      }`}
    >
      {/* card header */}
      <div className="px-3 pt-2.5 pb-1">
        <div className="flex items-start justify-between gap-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <Avatar name={comment.author_name} />
            <div className="min-w-0 leading-tight">
              <span className="text-[11px] font-semibold text-gray-800 block truncate capitalize">{shortName(comment.author_name)}</span>
              <span className="text-[10px] text-gray-400">{shortDate(comment.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {currentUserEmail === comment.author_name && (
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteRoot(); }}
                className="p-0.5 hover:bg-red-50 rounded transition-colors"
              >
                <Trash size={10} className="text-red-400" />
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-700 mt-1.5 leading-relaxed break-words">{comment.text}</p>
      </div>

      {/* replies list — always visible */}
      {comment.replies.length > 0 && (
        <div className="px-3 pb-1 space-y-2 border-t border-gray-50">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="flex gap-1.5 pt-2">
              <Avatar name={reply.author_name} size="xs" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <div className="min-w-0 leading-tight">
                    <span className="text-[10px] font-semibold text-gray-700 capitalize block truncate">{shortName(reply.author_name)}</span>
                    <span className="text-[9px] text-gray-400">{shortDate(reply.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-0.5 shrink-0">

                    {currentUserEmail === reply.author_name && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteReply(reply.id); }}
                        className="p-0.5 hover:bg-red-50 rounded"
                      >
                        <Trash size={9} className="text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[11px] text-gray-600 mt-0.5 leading-relaxed break-words">{reply.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* reply input — always visible */}
      <div
        className="border-t border-gray-100 p-2 flex gap-1.5 items-end"
        onClick={(e) => e.stopPropagation()}
      >
        <textarea
          ref={replyRef}
          value={replyText}
          onChange={(e) => {
            setReplyText(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleReply(); }
          }}
          placeholder="Reply"
          rows={1}
          className="w-full text-xs resize-none border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#1d365d] text-gray-700 placeholder-gray-400 overflow-hidden"
        />
      </div>
    </div>
  );
};

// ─── New comment form card ─────────────────────────────────────────────────────
const NewCommentCard: React.FC<{
  documentId: string;
  pendingPin: PendingPin;
  pages: string[];
  currentUserEmail: string | null;
  onCreated: (c: Comment) => void;
  onCancel: () => void;
}> = ({ documentId, pendingPin, currentUserEmail, onCreated, onCancel }) => {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);


  useEffect(() => { ref.current?.focus(); }, []);

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await createComment(documentId, {
        page_id: pendingPin.pageId,
        text: trimmed,
        x_position: pendingPin.xFrac,
        y_position: pendingPin.yFrac,
      });
      onCreated({
        id: data.id,
        page_id: data.page_id,
        author_id: data.author_id,
        author_name: data.author_name,
        text: data.text,
        x_position: data.x_position,
        y_position: data.y_position,
        created_at: data.created_at,
        replies: [],
      });
    } catch (e) {
      console.error("Failed to create comment", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-l-[3px] border-[#1d365d] rounded-r-lg bg-white shadow-md ring-1 ring-[#1d365d]/20 mb-2 mx-2 overflow-hidden">
      <div className="px-3 pt-2.5 pb-1">
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            {currentUserEmail && <Avatar name={currentUserEmail} />}
            <span className="text-[11px] font-semibold text-gray-700 capitalize">
              {currentUserEmail ? shortName(currentUserEmail) : "You"}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onCancel} className="p-0.5 hover:bg-gray-100 rounded">
              <X size={11} className="text-gray-400" />
            </button>
          </div>
        </div>
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
            if (e.key === "Escape") onCancel();
          }}
          placeholder="Add a comment..."
          rows={3}
          className="w-full text-xs resize-none border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#1d365d] text-gray-700 placeholder-gray-400"
        />
      </div>
      <div className="border-t border-gray-100 px-3 py-1.5 flex justify-end gap-2">
        <button onClick={onCancel} className="text-[11px] text-gray-500 hover:text-gray-700">Cancel</button>
        <button
          onClick={handleSubmit}
          disabled={!text.trim() || submitting}
          className="text-[11px] px-2.5 py-1 bg-[#1d365d] text-white rounded hover:bg-[#162a4a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Comment
        </button>
      </div>
    </div>
  );
};

// ─── Sidebar container ────────────────────────────────────────────────────────
const CommentSidebar: React.FC<Props> = ({
  comments,
  documentId,
  currentUserEmail,
  pendingPin,
  selectedCommentId,
  pages,
  onCommentsChanged,
  onPendingPinClear,
  onSelectComment,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!selectedCommentId) return;
    const el = cardRefs.current[selectedCommentId];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedCommentId]);

  useEffect(() => {
    if (pendingPin) setCollapsed(false);
  }, [pendingPin]);

  const sorted = [...comments].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const handleUpdated = (updated: Comment) =>
    onCommentsChanged(comments.map((c) => (c.id === updated.id ? updated : c)));

  const handleDeleted = (id: string) => {
    onCommentsChanged(comments.filter((c) => c.id !== id));
    if (selectedCommentId === id) onSelectComment(null);
  };

  const handleCreated = (c: Comment) => {
    onCommentsChanged([...comments, c]);
    onPendingPinClear();
    onSelectComment(c.id);
  };

  /* ── Collapsed strip ── */
  if (collapsed) {
    return (
      <div className="w-10 shrink-0 flex flex-col items-center border-r border-gray-200 bg-gray-50 overflow-hidden">
        <button
          onClick={() => setCollapsed(false)}
          className="p-2.5 hover:bg-gray-100 transition-colors"
          title="Show comments"
        >
          <MessageSquare size={15} className="text-gray-500" />
        </button>
        <div className="flex flex-col items-center gap-1.5 mt-1">
          {sorted.map((comment) => (
            <button
              key={comment.id}
              onClick={() => { setCollapsed(false); onSelectComment(comment.id); }}
              title={comment.author_name}
              className="shrink-0"
            >
              <Avatar name={comment.author_name} />
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ── Expanded sidebar ── */
  return (
    <div className="w-56 shrink-0 flex flex-col border-r border-gray-200 bg-gray-50 overflow-hidden">
      {/* header */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between bg-white shrink-0">
        <div className="flex items-center gap-1.5">
          <MessageSquare size={13} className="text-gray-500" />
          <span className="text-xs font-semibold text-gray-600">
            {comments.length === 0 ? "Comments" : `${comments.length} comment${comments.length !== 1 ? "s" : ""}`}
          </span>
        </div>
        <button onClick={() => setCollapsed(true)} className="p-0.5 hover:bg-gray-100 rounded transition-colors" title="Collapse">
          <ChevronLeft size={13} className="text-gray-400" />
        </button>
      </div>

      {/* scrollable list */}
      <div
        className="flex-1 overflow-y-auto py-2"
        onClick={() => onSelectComment(null)}
      >
        {pendingPin && (
          <NewCommentCard
            documentId={documentId}
            pendingPin={pendingPin}
            pages={pages}
            currentUserEmail={currentUserEmail}
            onCreated={handleCreated}
            onCancel={onPendingPinClear}
          />
        )}

        {sorted.length === 0 && !pendingPin && (
          <div className="px-4 py-6 text-center">
            <MessageSquare size={20} className="text-gray-300 mx-auto mb-2" />
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Click the comment icon in the toolbar, then click anywhere on the document to add a comment.
            </p>
          </div>
        )}

        {sorted.map((comment) => (
          <div key={comment.id} ref={(el) => { cardRefs.current[comment.id] = el; }}>
            <CommentCard
              comment={comment}
              documentId={documentId}
              currentUserEmail={currentUserEmail}
              isSelected={selectedCommentId === comment.id}
              onSelect={() => onSelectComment(comment.id === selectedCommentId ? null : comment.id)}
              onUpdated={handleUpdated}
              onDeleted={handleDeleted}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommentSidebar;
